import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { Zap, ChevronDown, ArrowRight } from 'lucide-react';

const AutocompleteInput = ({ value, onChange, placeholder, suggestions }) => {
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [filteredSuggestions, setFilteredSuggestions] = useState(suggestions);

    const handleInputChange = (e) => {
        const inputValue = e.target.value;
        onChange(e);
        if (inputValue.trim() === '') {
            setFilteredSuggestions(suggestions);
        } else {
            const filtered = suggestions.filter(s => 
                s.toLowerCase().includes(inputValue.toLowerCase())
            );
            setFilteredSuggestions(filtered);
        }
    };

    const handleFocus = () => {
        setShowSuggestions(true);
        setFilteredSuggestions(suggestions); // Show all suggestions on focus
    };

    return (
        <div className="relative">
            <input
                type="text"
                value={value}
                onChange={handleInputChange}
                onFocus={handleFocus}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                placeholder={placeholder}
                className="w-full px-5 py-4 pr-12 border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            />
            <ChevronDown 
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" 
                size={20} 
            />
            {showSuggestions && filteredSuggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-2 bg-white border-2 border-blue-200 rounded-2xl shadow-2xl max-h-60 overflow-y-auto">
                    {filteredSuggestions.map((suggestion, idx) => (
                        <button
                            key={idx}
                            type="button"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                onChange({ target: { value: suggestion } });
                                setShowSuggestions(false);
                            }}
                            className="w-full px-5 py-3 text-left hover:bg-blue-50 transition-colors text-gray-700 hover:text-blue-700 font-medium first:rounded-t-2xl last:rounded-b-2xl"
                        >
                            {suggestion}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const ProfileOptionalStep = ({ profileData, setProfileData, onNext, onSkip }) => {
    const splitIndustries = (value) => String(value || '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);

    // Determine currency based on location
    const getCurrency = () => {
        const location = profileData.locationPreference?.toLowerCase() || '';
        if (location.includes('uk') || location.includes('london')) return '£';
        if (location.includes('canada') || location.includes('toronto')) return 'CAD $';
        if (location.includes('australia') || location.includes('sydney')) return 'AUD $';
        if (location.includes('singapore')) return 'SGD $';
        if (location.includes('germany') || location.includes('berlin') || location.includes('europe')) return '€';
        return '$'; // Default to USD
    };

    const currency = getCurrency();

    const formContent = (
    <div className="space-y-5 max-w-3xl mx-auto">
        <div className="mb-4">
            <p className="font-semibold text-gray-900 text-lg mb-1">Optional Preferences</p>
            <p className="text-gray-600 text-sm">
                Help refine your job recommendations. You can update these later.
            </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
            <div>
                <label className="block text-sm font-bold text-gray-900 mb-3">
                    Industry Preference
                </label>
                <AutocompleteInput
                    value={profileData.industryPreference}
                    onChange={(e) => setProfileData({
                        ...profileData,
                        industryPreference: e.target.value,
                        preferredIndustries: splitIndustries(e.target.value),
                        preferredIndustry: splitIndustries(e.target.value)
                    })}
                    placeholder="e.g., Technology, Healthcare, Finance"
                    suggestions={[
                        'Technology',
                        'Software & IT Services',
                        'Healthcare',
                        'Biotechnology',
                        'Pharmaceuticals',
                        'Finance',
                        'Banking',
                        'Investment',
                        'Insurance',
                        'E-commerce',
                        'Retail',
                        'Education',
                        'EdTech',
                        'Manufacturing',
                        'Automotive',
                        'Aerospace',
                        'Energy',
                        'Renewable Energy',
                        'Oil & Gas',
                        'Media & Entertainment',
                        'Gaming',
                        'Advertising',
                        'Marketing',
                        'Public Relations',
                        'Consulting',
                        'Management Consulting',
                        'Real Estate',
                        'Construction',
                        'Telecommunications',
                        'Transportation & Logistics',
                        'Travel & Hospitality',
                        'Food & Beverage',
                        'Agriculture',
                        'Legal Services',
                        'Accounting',
                        'Architecture',
                        'Design',
                        'Non-Profit',
                        'Government',
                        'Defense',
                        'Cybersecurity',
                        'Artificial Intelligence',
                        'Blockchain & Crypto',
                        'SaaS',
                        'Cloud Computing',
                    ]}
                />
            </div>

            <div>
                <label className="block text-sm font-bold text-gray-900 mb-3">
                    Company Size Preference
                </label>
                <select
                    value={profileData.companySizePreference}
                    onChange={(e) => setProfileData({ ...profileData, companySizePreference: e.target.value })}
                    className="w-full px-5 py-4 border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                >
                    <option value="">No preference</option>
                    <option value="startup">Startup (1-50 employees)</option>
                    <option value="small">Small (51-200 employees)</option>
                    <option value="medium">Medium (201-1000 employees)</option>
                    <option value="large">Large (1000+ employees)</option>
                </select>
            </div>

            <div>
                <label className="block text-sm font-bold text-gray-900 mb-3">
                    Salary Expectation {currency && <span className="text-blue-600">({currency})</span>}
                </label>
                <input
                    type="text"
                    value={profileData.salaryExpectation}
                    onChange={(e) => setProfileData({ ...profileData, salaryExpectation: e.target.value })}
                    placeholder={`e.g., ${currency}80,000 - ${currency}120,000`}
                    className="w-full px-5 py-4 border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                />
            </div>

            <div>
                <label className="block text-sm font-bold text-gray-900 mb-3">
                    Remote Work Preference
                </label>
                <select
                    value={profileData.remotePreference}
                    onChange={(e) => setProfileData({ ...profileData, remotePreference: e.target.value })}
                    className="w-full px-5 py-4 border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                >
                    <option value="">No preference</option>
                    <option value="remote">Remote only</option>
                    <option value="hybrid">Hybrid (2-3 days office)</option>
                    <option value="onsite">On-site</option>
                </select>
            </div>
        </div>
    </div>
    );
    
    const content = (
        <>
            <div className="fixed inset-0 bg-slate-100/35 backdrop-blur-md z-[99998]" />
            <div className="fixed inset-0 z-[99999] flex items-center justify-center pointer-events-none">
                <motion.div
                    initial={{ opacity: 0, scale: 0.9, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9, y: 20 }}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                    className="relative bg-white rounded-xl shadow-lg border border-gray-200 p-6 max-w-3xl mx-4 pointer-events-auto max-h-[90vh] overflow-y-auto"
                >
                    {formContent}
                    <div className="flex items-center gap-3 justify-center mt-6 pt-5 border-t border-gray-100">
                        {onSkip && (
                            <button
                                onClick={onSkip}
                                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 font-medium transition-colors"
                            >
                                Skip
                            </button>
                        )}
                        <button
                            onClick={onNext}
                            className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                        >
                            Continue
                            <ArrowRight size={16} />
                        </button>
                    </div>
                </motion.div>
            </div>
        </>
    );
    
    return typeof document !== 'undefined' 
        ? createPortal(content, document.body)
        : formContent;
};

export default ProfileOptionalStep;
